import { createContext, useState, useRef, useCallback, useEffect } from 'react';
import './App.css';
import "./portal.css";
import "./screens/personal_evaluation/shared/style/form_style.css";
import { useNavigate, useLocation } from 'react-router-dom';
import IndexesRoutes from './routes';
import PrivilegeService from './services/PrivilegeService';
import AuthService from './services/AuthService';
import UserService from './services/UserService';
import TokenExpirationNotification from './components/TokenExpirationNotification';
import useTokenMonitor from './hooks/useTokenMonitor';

export const userDetailContext = createContext(null);

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const [userInfo, setUserInfo] = useState({ id: '', privileges: [] });
  const isMountedRef = useRef(true);
  
  // Surveillance du token
  useTokenMonitor(30000);

  // Fonction de déconnexion 
  const handleLogout = useCallback(() => {
    if (!isMountedRef.current) return;
    AuthService.logout();
  
  }, []);

  // Charger les privilèges
  const loadPrivilege = useCallback(async () => {
    try {
      const result = await PrivilegeService.getUserPrivilege();
      if (!isMountedRef.current) return;
      
      if (result && result.data) {
        const privileges = result.data.map(d => ({
          id: d.privilege_id,
          label: d.name
        }));
        setUserInfo(prev => ({ ...prev, privileges }));
        localStorage.setItem("USER_PRIVILEGES", JSON.stringify(privileges));
      } else if (result && result.logout) {
        handleLogout();
      }
    } catch (error) {
      if (error.response?.status === 401) {
        handleLogout();
      }
      console.error("Erreur chargement privilèges:", error);
    }
  }, [handleLogout]);


  const loadUserInfo = useCallback(() => {
    const user = localStorage.getItem("USER");
    if (!user) return;
    
    UserService.getUserSummaryByUserId(user)
      .then((result) => {
        if (result && result.status && isMountedRef.current) {
          localStorage.setItem("USER_DIR", result.data.direction_id);
          localStorage.setItem("USER_DIR_CODE", `${result.data.direction_name} - ${result.data.direction_code}`);
          localStorage.setItem("USER_DETAILS", JSON.stringify(result.data));
        }
      })
      .catch(error => {
        console.error("Erreur lors du chargement des infos utilisateur:", error);
      });
  }, []);


  useEffect(() => {
    isMountedRef.current = true;

    const initAuth = async () => {
      // Pages publiques
      const publicPaths = ['/', '/login', '/new-password'];
      if (publicPaths.includes(location.pathname)) {
        return;
      }

      if (AuthService.isAuthenticated()) {
        await loadPrivilege();
        loadUserInfo();
      } else {
        handleLogout();
      }
    };

    initAuth();

    return () => {
      isMountedRef.current = false;
    };

  }, [location.pathname]);

useEffect(() => {
  const publicPaths = ['/', '/login', '/new-password', '/reset-password'];
  
   // Vérifier si la route actuelle est une page publique (avec ou sans paramètres)
   const isPublicPage = publicPaths.some(path => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname === path || location.pathname.startsWith(path + '/');
  });
  
  const isAuthenticated = AuthService.isAuthenticated();

  // Si pas auth ET pas sur une page publique
  if (!isAuthenticated && !isPublicPage) {
    navigate('/', { replace: true });
  }
  
  // Si auth ET sur la page d'accueil ("/") 
  if (isAuthenticated && location.pathname === '/') {
    navigate('/dashboard', { replace: true }); 
  }
}, [location.pathname, navigate]);

  return (
    <div className="App">
      <TokenExpirationNotification />
      <userDetailContext.Provider value={userInfo}>
        <IndexesRoutes />
      </userDetailContext.Provider>
    </div>
  );
}

export default App;
